import * as React from 'react';

import { useApplicationContext } from '../../../utils/applicationContext';
import { releaseOnboarding, requestOnboarding } from '../../../utils/onboardingCoordinator';
import {
  AUTO_CLOSE_ONBOARDING_DELAY_MS,
  COMPARE_ONBOARDING_ENABLED_CONFIG,
  COMPARE_ONBOARDING_ID,
  SHOW_ONBOARDING_DELAY_MS,
} from '../constants/compare';
import { getCompareOnboardingCookie, setCompareOnboardingCookie } from '../utils/compareOnboardingCookie';

interface IUseCompareOnboardingResult {
  open: boolean;
  closeHandler(): void;
}

export function useCompareOnboarding(enabled: boolean): IUseCompareOnboardingResult {
  const { config } = useApplicationContext();
  const [open, setOpen] = React.useState(false);

  const closeHandler = React.useCallback(() => {
    setOpen(false);
    releaseOnboarding(COMPARE_ONBOARDING_ID);
  }, []);

  React.useEffect(() => {
    const isOnboardingEnabled = config.get<boolean>(COMPARE_ONBOARDING_ENABLED_CONFIG) || false;

    if (!enabled || !isOnboardingEnabled || getCompareOnboardingCookie()) {
      return;
    }

    const timerId = setTimeout(() => {
      if (requestOnboarding(COMPARE_ONBOARDING_ID)) {
        setOpen(true);
        setCompareOnboardingCookie();
      }
    }, SHOW_ONBOARDING_DELAY_MS);

    return () => clearTimeout(timerId);
  }, [config, enabled]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const timerId = setTimeout(closeHandler, AUTO_CLOSE_ONBOARDING_DELAY_MS);
    const closeByDocumentClick = () => closeHandler();

    document.addEventListener('pointerdown', closeByDocumentClick, true);
    document.addEventListener('click', closeByDocumentClick, true);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('pointerdown', closeByDocumentClick, true);
      document.removeEventListener('click', closeByDocumentClick, true);
    };
  }, [closeHandler, open]);

  React.useEffect(() => () => releaseOnboarding(COMPARE_ONBOARDING_ID), []);

  return { open, closeHandler };
}
